/**
 * Taxonomy Error Recovery Service
 *
 * Provides fallback data and recovery strategies when the taxonomy system
 * encounters errors. This service is designed to work with the useTaxonomy hook
 * and the TaxonomyInitProvider to ensure a graceful experience even when
 * taxonomy data cannot be loaded properly.
 */
import { TaxonomyItem } from '../types/taxonomy.types';
import { logger } from '../utils/logger';

// Define types for the fallback data structures
type TaxonomyItemArray = Array<{
  id: number;
  code: string;
  numericCode: string;
  name: string;
}>;

interface CategoryMap {
  [key: string]: TaxonomyItemArray;
}

interface SubcategoryMap {
  [key: string]: TaxonomyItemArray;
}

interface MfaMap {
  [key: string]: string;
}

// Fallback data for critical taxonomy paths
const FALLBACK_DATA = {
  CATEGORIES: {
    S: [
      // Songs
      { id: 4, code: 'POP', numericCode: '004', name: 'Pop' },
      { id: 5, code: 'RCK', numericCode: '005', name: 'Rock' },
      { id: 6, code: 'HIP', numericCode: '006', name: 'Hip Hop' },
    ],
    W: [
      // Worlds
      { id: 4, code: 'BCH', numericCode: '004', name: 'Beach' },
      { id: 5, code: 'CST', numericCode: '005', name: 'Coast' },
      { id: 6, code: 'DCL', numericCode: '006', name: 'Dance Club' },
    ],
    G: [
      // Genesis
      { id: 1, code: 'GRD', numericCode: '001', name: 'Ground' },
      { id: 2, code: 'OBJ', numericCode: '002', name: 'Object' },
    ],
    L: [
      // Looks
      { id: 1, code: 'OUT', numericCode: '001', name: 'Outfit' },
      { id: 2, code: 'ACC', numericCode: '002', name: 'Accessory' },
    ],
    M: [
      // Moves
      { id: 1, code: 'DNC', numericCode: '001', name: 'Dance' },
      { id: 2, code: 'ACT', numericCode: '002', name: 'Action' },
    ],
  } as CategoryMap,
  SUBCATEGORIES: {
    'S.POP': [
      // Song - Pop
      { id: 1, code: 'BPP', numericCode: '001', name: 'Bubblegum Pop' },
      { id: 2, code: 'ELP', numericCode: '002', name: 'Electro Pop' },
      { id: 3, code: 'HPM', numericCode: '003', name: 'Hip Pop Music' },
    ],
    'S.RCK': [
      // Song - Rock
      { id: 1, code: 'BAS', numericCode: '001', name: 'Bass' },
      { id: 2, code: 'ALT', numericCode: '002', name: 'Alternative' },
    ],
    'W.BCH': [
      // World - Beach
      { id: 3, code: 'SUN', numericCode: '003', name: 'Sunset' },
      { id: 3, code: 'FES', numericCode: '003', name: 'Festival' },
      { id: 2, code: 'TRO', numericCode: '002', name: 'Tropical' },
    ],
  } as SubcategoryMap,
  MFA_MAPPINGS: {
    'W.BCH.SUN': '5.004.003',
    'S.POP.HPM': '2.004.003',
  } as MfaMap,
};

// Maximum number of retries for loading taxonomy data
const MAX_RETRIES = 3;

// Record to track repeated errors to prevent repeated fallback logging
const errorTrackingMap = new Map<string, number>();

/**
 * Get fallback categories for a layer when primary data fails to load
 */
export function getFallbackCategories(layer: string): TaxonomyItem[] {
  const key = `categories-${layer}`;
  const errorCount = (errorTrackingMap.get(key) || 0) + 1;
  errorTrackingMap.set(key, errorCount);
  
  if (errorCount === 1) {
    logger.warn(
      `Using fallback categories for layer ${layer} (attempt ${errorCount})`
    );
  }
  
  // Convert fallback data to TaxonomyItem[] format with id property
  const fallbackCategories = FALLBACK_DATA.CATEGORIES[layer] || [];
  return fallbackCategories.map(cat => ({
    id: cat.id || parseInt(cat.numericCode) || 0,
    code: cat.code,
    name: cat.name,
    numericCode: cat.numericCode,
  }));
}

/**
 * Get fallback subcategories for a layer/category when primary data fails to load
 */
export function getFallbackSubcategories(
  layer: string,
  category: string
): TaxonomyItem[] {
  const key = `subcategories-${layer}.${category}`;
  const errorCount = (errorTrackingMap.get(key) || 0) + 1;
  errorTrackingMap.set(key, errorCount);
  
  if (errorCount === 1) {
    logger.warn(
      `Using fallback subcategories for ${layer}.${category} (attempt ${errorCount})`
    );
  }
  
  // Convert fallback data to TaxonomyItem[] format with id property
  const fallbackSubcategories = FALLBACK_DATA.SUBCATEGORIES[`${layer}.${category}`] || [];
  return fallbackSubcategories.map(sub => ({
    id: sub.id || parseInt(sub.numericCode) || 0,
    code: sub.code,
    name: sub.name,
    numericCode: sub.numericCode,
  }));
}

/**
 * Get fallback MFA code for a given layer, category, and subcategory
 */
export function getFallbackMFA(
  layer: string,
  category: string,
  subcategory: string,
  sequential: string,
  fileType?: string
): string {
  const key = `mfa-${layer}.${category}.${subcategory}`;
  const errorCount = (errorTrackingMap.get(key) || 0) + 1;
  errorTrackingMap.set(key, errorCount);

  if (errorCount === 1) {
    logger.warn(
      `Using fallback MFA mapping for ${layer}.${category}.${subcategory} (attempt ${errorCount})`
    );
  }

  const fallbackKey = `${layer}.${category}.${subcategory}`;
  const baseMapping = FALLBACK_DATA.MFA_MAPPINGS[fallbackKey];

  if (!baseMapping) {
    return '';
  }

  return `${baseMapping}.${sequential}${fileType ? `.${fileType}` : ''}`;
}

/**
 * Check if maximum retries have been reached for a specific operation
 */
export function hasReachedMaxRetries(
  operationType: string,
  identifier: string
): boolean {
  const key = `${operationType}-${identifier}`;
  const currentRetries = errorTrackingMap.get(key) || 0;
  return currentRetries >= MAX_RETRIES;
}

/**
 * Increment retry count for a specific operation
 */
export function incrementRetryCount(
  operationType: string,
  identifier: string
): number {
  const key = `${operationType}-${identifier}`;
  const currentRetries = (errorTrackingMap.get(key) || 0) + 1;
  errorTrackingMap.set(key, currentRetries);
  return currentRetries;
}

/**
 * Reset retry counts for all operations or a specific one
 */
export function resetRetryCount(
  operationType?: string,
  identifier?: string
): void {
  if (operationType && identifier) {
    const key = `${operationType}-${identifier}`;
    errorTrackingMap.delete(key);
  } else {
    errorTrackingMap.clear();
  }
}

/**
 * Get user-friendly error message for taxonomy errors
 */
export function getUserFriendlyErrorMessage(
  error: Error,
  context: string
): string {
  // Extract the base message without stack trace
  const baseMessage = error.message.split('\n')[0];

  switch (context) {
    case 'initialization':
      return `Unable to initialize taxonomy data. Some features may be limited. (${baseMessage})`;
    case 'categories':
      return `Unable to load categories. Using fallback data if available. (${baseMessage})`;
    case 'subcategories':
      return `Unable to load subcategories. Using fallback data if available. (${baseMessage})`;
    case 'mfa-conversion':
      return `Unable to generate NNA Address. Using fallback mapping if available. (${baseMessage})`;
    default:
      return `Taxonomy error: ${baseMessage}`;
  }
}

// Export a singleton instance
export const taxonomyErrorRecovery = {
  getFallbackCategories,
  getFallbackSubcategories,
  getFallbackMFA,
  hasReachedMaxRetries,
  incrementRetryCount,
  resetRetryCount,
  getUserFriendlyErrorMessage,
};
